import { createElement, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as errors from './errors.js';
export { errors as Errors };
export { Type as SourceType } from './source.js';
export { Loader } from './loader.js';
import { LoaderContext, TranslateContext } from './context.js';
export { useTranslate } from './use-translate.js';

/**
 * React Context provider for translation loader
 *
 * Enables content fetching for other parts of i18n API thus must be wrapping
 * {@link TranslationProvider} and components that use {@link useTranslate}
 * hook.
 *
 * @example
 * const loader = Herz.I18n.Loader({ /* loader config *\/ })
 *
 * ReactDOM.render(
 *   <Herz.I18n.LoaderProvider loader={loader}>
 *     <App />
 *   </Herz.I18n.LoaderProvider>
 * )
 *
 * @param {Object} props Component props
 * @param {!Loader} props.loader Translation loader instance
 *
 * @access public
 */

function LoaderProvider(props) {
  return /*#__PURE__*/createElement(LoaderContext.Provider, {
    value: props.loader
  }, props.children);
}
LoaderProvider.propTypes = {
  children: PropTypes.node,
  loader: PropTypes.object.isRequired
};
/**
 * React Context provider for translation APIs
 *
 * @example
 * function TranslationProvider(props) {
 *   const globals = React.useMemo(() => ({
 *     brandName: props.brandName,
 *     supportEmail: props.supportEmail,
 *   }), [props.brandName, props.supportEmail])
 *
 *   const processors = React.useMemo(() => ({
 *     currency: createCurrencyProcessor(props.currency),
 *     uppercase: R.toUpper,
 *   }), [props.currency])
 *
 *   return (
 *     <Herz.I18n.TranslationProvider
 *       globals={globals}
 *       language="en-DE"
 *       processors={processors}
 *     >
 *       {props.children}
 *     </Herz.I18n.TranslationProvider>
 *   )
 * }
 *
 * @param {Object} props Component props
 * @param {?Object.<string, *>} props.globals Global variables that will be
 * available for every key. Ensure passed object is **memoized** (or is static)
 * to avoid potential rendering loops.
 * @param {!string|!Array.<!string>} props.language Language to fetch
 * translations in
 * @param {?Object.<string, function>} props.processors Variable processors
 * that will be available for every key. Ensure passed object is **memoized**
 * (or is static) to avoid potential rendering loops.
 *
 * @access public
 */

function TranslationProvider(props) {
  var loader = useContext(LoaderContext);
  var getTranslate = useMemo(() => loader.init({
    globals: props.globals,
    language: props.language,
    processors: props.processors
  }), [props.globals, props.language, props.processors]);
  return /*#__PURE__*/createElement(TranslateContext.Provider, {
    value: getTranslate
  }, props.children);
}
TranslationProvider.defaultProps = {
  globals: {},
  processors: {}
};
TranslationProvider.propTypes = {
  children: PropTypes.node,
  globals: PropTypes.object,
  language: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  loader: PropTypes.shape({
    getTranslate: PropTypes.func.isRequired
  }),
  processors: PropTypes.object
};

export { LoaderProvider, TranslationProvider };
